// @flow

import React, { useCallback, useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { TransitionGroup } from 'react-transition-group';
import type { Coordinate, AudioAssets } from './types';
import audioPlaylist from '../utils/audio_playlist';
import CoordinateLibrary from './NewCoordinateLibrary';
import HighlightCoordinate from './HighlightCoordinate';
import useGameState from '../utils/use_game_state';
import Game from '../Shared/Game';

const CLICK_RADIUS = 80; // Radius in pixels from the center where a click is valid.

type Props = {
  picture: string,
  coordinates: Array<Coordinate>,
  assetPath: string,
  title: string,
  back: string,
  audio: AudioAssets,
  nextGamePath: string,
};

const dist = (point1: Coordinate, point2: Coordinate): number =>
  Math.sqrt(
    ((point2.x - point1.x) * (point2.x - point1.x)) +
    ((point2.y - point1.y) * (point2.y - point1.y)),
  );

const PictureSearch = (props: Props) => {
  const imageRef = useRef(null);
  const uploadImg: Image = new Image();
  uploadImg.src = props.picture;
  const [coordinates, setCoordinates] = useState(props.coordinates);
  const [playing, setPlaying] = useState(true);
  const [highlights, setHighlights] = useState([]);
  const [playRight, setPlayRight] = useState(null);
  const [playWrong, setPlayWrong] = useState(null);
  const [playDone, setPlayDone] = useState(null);
  const loadAssets = useCallback(() => [
    audioPlaylist(props.audio.good).then(audioPlayer => setPlayRight(() => audioPlayer)),
    audioPlaylist(props.audio.wrong).then(audioPlayer => setPlayWrong(() => audioPlayer)),
    audioPlaylist([props.audio.done]).then(audioPlayer => setPlayDone(() => audioPlayer)),
  ], [props.audio]);
  const [gameState, setGameDone] = useGameState(loadAssets);

  // Finds all coordinates that lie within a radius from the clicked location and
  // returns them with the index added.
  const findMatchingCoordinate = (coord: Coordinate): ?Coordinate => coordinates
    .map(c => ({ distance: dist(c, coord), coord: c }))
    .filter(c => c.distance <= CLICK_RADIUS)
    .sort((ca, cb) => ca.distance - cb.distance)
    .map(c => c.coord)[0];

  // Filters the clicked coordinates away from the state and checks if the games ends.
  const removeCoordsFromState = (coordsIndexes: Array<number>) => {
    setCoordinates(coordinates.filter((_item, index) => !coordsIndexes.includes(index)));
  };

  useEffect(() => {
    if (playing && coordinates.length <= 0) {
      playDone();
      setGameDone();
      setPlaying(false);
    }
  }, [playing, coordinates, playDone, setGameDone]);

  // Adds fading style to clicked coordinate icons and removes them from state after a little while.
  const removeItem = (coord: Coordinate) => {
    const coordIndex = coordinates.findIndex(c => c.x === coord.x && c.y === coord.y);
    const coordIconEle = document.querySelector(`#icon-${coordIndex}`);
    if (coordIconEle) {
      coordIconEle.style.transition = 'all 1000ms ease-in';
      coordIconEle.style.opacity = '0';
    }
    // Removing from state is pretty much instant and we want to wait for the fading to finish,
    // therefore the timeout.
    setTimeout(() => {
      removeCoordsFromState([coordIndex]);
    }, 1000);
  };

  // Handle for when a click in the picture is registered.
  const clickHandle = (e: SyntheticMouseEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const rect = img.getBoundingClientRect();
    const tempX = uploadImg.width / rect.width;
    const tempY = uploadImg.height / rect.height;
    const coord = { x: (e.clientX - rect.left) * tempX, y: (e.clientY - rect.top) * tempY };
    const found = findMatchingCoordinate(coord);
    if (found) {
      playRight();
      setHighlights([...highlights, found]);
      removeItem(found);
    } else {
      playWrong();
    }
  };

  return (
    <Game
      gameState={gameState}
      relatedGames={props.relatedGames}
      assetPath={props.assetPath}
      isApp={props.isApp}
    >
      {playing &&
        <>
          <div className="ps__base flex flex-col justify-center items-center h-full space-y-4">
            <CoordinateLibrary
              assetPath={props.assetPath}
              coordinates={coordinates}
              picture={uploadImg}
            />
            <div className="ps__image relative mx-auto px-1 w-full overflow-auto flex flex-col items-center">
              <img
                ref={imageRef}
                src={props.picture}
                className="rounded-lg max-w-full max-h-full border-8 border-white shadow border-solid"
                alt="zoekplaatje"
                onClick={clickHandle}
              />
            </div>
          </div>
          <TransitionGroup>
            {highlights.map((item, index) => (
              <HighlightCoordinate
                coord={item}
                key={index}
                img={uploadImg}
                picture={imageRef}
              />
            ))}
          </TransitionGroup>
        </>
      }
    </Game>
  );
};

const pictureSearchElement = document.querySelector('#picture_search');
if (pictureSearchElement) {
  const data = pictureSearchElement.dataset;
  const props = {
    title: data.title,
    audio: JSON.parse(data.audio),
    back: data.back,
    picture: data.picture,
    coordinates: data.coordinates ? JSON.parse(data.coordinates) : [],
    assetPath: data.assetPath,
    nextGamePath: data.nextGamePath,
    relatedGames: JSON.parse(data.related),
    isApp: data.app === 'true',
  };
  ReactDOM.render(<PictureSearch {...props} />, pictureSearchElement);
}
